"use client";

import { useState, useEffect, useRef } from "react";
import { useTranslations, useLocale } from "next-intl";
import Image from "next/image";
import { Link, useRouter, usePathname } from "@/i18n/navigation";
import { routing, type Locale } from "@/i18n/routing";
import {
  FiX,
  FiChevronDown,
  FiChevronRight,
  FiCalendar,
  FiMessageCircle,
  FiPhone,
  FiMail,
  FiHome,
  FiInfo,
  FiBriefcase,
  FiStar,
  FiTruck,
  FiUsers,
  FiHelpCircle,
  FiMapPin,
  FiFacebook,
  FiInstagram,
  FiYoutube,
  FiGlobe,
  FiExternalLink,
} from "react-icons/fi";
import type { IconType } from "react-icons";

interface NavItem {
  key: string;
  href: string;
  external?: boolean;
  label?: string;
  children?: { key: string; href: string }[];
}

interface MobileMenuProps {
  open: boolean;
  onClose: () => void;
  navItems: NavItem[];
}

const navIcons: Record<string, IconType> = {
  home: FiHome,
  about: FiInfo,
  services: FiBriefcase,
  brands: FiStar,
  transfers: FiTruck,
  tours: FiStar,
  fleet: FiTruck,
  groupBooking: FiUsers,
  faq: FiHelpCircle,
  contact: FiMail,
};

const navGroups: { label: string; keys: string[] }[] = [
  { label: "Discover", keys: ["about", "services", "brands"] },
  { label: "Book", keys: ["transfers", "tours", "fleet", "groupBooking"] },
  { label: "Help", keys: ["faq", "contact"] },
];

const localeLabels: Record<string, string> = {
  en: "EN",
  es: "ES",
  de: "DE",
  nl: "NL",
  pap: "PAP",
};
const localeNames: Record<string, string> = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  nl: "Nederlands",
  pap: "Papiamentu",
};

export default function MobileMenu({ open, onClose, navItems }: MobileMenuProps) {
  const t = useTranslations("nav");
  const pathname = usePathname();
  const locale = useLocale();
  const router = useRouter();
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const prevPathname = useRef(pathname);

  useEffect(() => {
    if (prevPathname.current !== pathname) {
      prevPathname.current = pathname;
      onClose();
    }
  }, [pathname, onClose]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      setLangOpen(false);
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  function switchLocale(newLocale: Locale) {
    router.replace(pathname, { locale: newLocale });
    setLangOpen(false);
    onClose();
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-[60] bg-fbtt-navy/70 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <div
        className={`fixed right-0 top-0 z-[70] flex h-full w-full max-w-[380px] transform flex-col bg-white shadow-2xl transition-transform duration-300 lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        {/* Header — navy gradient with brand logo + close */}
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-fbtt-navy via-fbtt-navy-dark to-fbtt-navy">
          <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-fbtt-orange/20 blur-3xl" />
          <div className="absolute -bottom-6 -left-6 h-32 w-32 rounded-full bg-fbtt-sky/20 blur-2xl" />
          <div className="relative flex items-center justify-between px-5 pt-5 pb-4">
            <Link
              href="/"
              className="flex items-center"
              aria-label="FBTT Travel home"
              onClick={onClose}
            >
              <Image
                src="/images/fbtt-logo-white.png"
                alt="FBTT Travel"
                width={240}
                height={85}
                className="h-9 w-auto"
                priority
              />
            </Link>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-white/20 transition-colors hover:bg-white/20"
              aria-label="Close menu"
            >
              <FiX className="h-5 w-5" />
            </button>
          </div>
          <div className="relative px-5 pb-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-fbtt-orange">
              We go the extra mile for you
            </p>
            <p className="mt-0.5 text-sm text-white/80">Curaçao&apos;s largest tour and transport operator</p>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid shrink-0 grid-cols-3 gap-2 bg-fbtt-light p-3">
          <Link
            href="/transfers/request"
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-fbtt-orange px-3 py-3.5 text-white shadow-sm transition-transform active:scale-95"
          >
            <FiCalendar className="h-5 w-5" />
            <span className="text-[11px] font-bold uppercase tracking-wide">Book</span>
          </Link>
          <a
            href="https://wa.me/59995146060?text=Hi!%20I%27d%20like%20to%20book%20a%20transfer."
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-[#25D366] px-3 py-3.5 text-white shadow-sm transition-transform active:scale-95"
          >
            <FiMessageCircle className="h-5 w-5" />
            <span className="text-[11px] font-bold uppercase tracking-wide">WhatsApp</span>
          </a>
          <a
            href="tel:+59998699559"
            className="flex flex-col items-center justify-center gap-1.5 rounded-xl bg-fbtt-teal px-3 py-3.5 text-white shadow-sm transition-transform active:scale-95"
          >
            <FiPhone className="h-5 w-5" />
            <span className="text-[11px] font-bold uppercase tracking-wide">Call</span>
          </a>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          {/* Grouped nav */}
          <nav className="px-4 py-5 space-y-5">
            {navGroups.map((group) => {
              const groupItems = group.keys
                .map((k) => navItems.find((i) => i.key === k))
                .filter((i): i is NavItem => Boolean(i));
              if (groupItems.length === 0) return null;
              return (
                <div key={group.label}>
                  <p className="mb-2 px-2 text-[11px] font-bold uppercase tracking-widest text-fbtt-teal">
                    {group.label}
                  </p>
                  <div className="space-y-0.5">
                    {groupItems.map((item) => {
                      const Icon = navIcons[item.key] ?? FiChevronRight;
                      const isActive = pathname === item.href;
                      if (item.children) {
                        const expanded = expandedItem === item.key;
                        return (
                          <div key={item.key}>
                            <button
                              onClick={() => setExpandedItem(expanded ? null : item.key)}
                              className={`flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-[15px] font-semibold transition-colors active:bg-fbtt-light ${
                                isActive ? "bg-fbtt-orange/10 text-fbtt-orange" : "text-fbtt-navy"
                              }`}
                            >
                              <Icon className="h-5 w-5 shrink-0 text-fbtt-orange" />
                              <span className="flex-1 text-left">{t(item.key)}</span>
                              <FiChevronDown
                                className={`h-4 w-4 text-fbtt-gray transition-transform duration-200 ${
                                  expanded ? "rotate-180" : ""
                                }`}
                              />
                            </button>
                            {expanded && (
                              <div className="ml-6 mt-1 space-y-0.5 border-l-2 border-fbtt-orange/30 pl-3">
                                {item.children.map((child) => (
                                  <Link
                                    key={child.key}
                                    href={child.href}
                                    className={`block rounded-lg px-3 py-2.5 text-[14px] transition-colors active:bg-fbtt-light ${
                                      pathname === child.href
                                        ? "font-semibold text-fbtt-orange"
                                        : "text-fbtt-gray"
                                    }`}
                                  >
                                    {t(child.key)}
                                  </Link>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      }
                      if (item.external) {
                        return (
                          <a
                            key={item.key}
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 rounded-xl px-3 py-3.5 text-[15px] font-semibold text-fbtt-navy transition-colors active:bg-fbtt-light"
                          >
                            <Icon className="h-5 w-5 shrink-0 text-fbtt-orange" />
                            <span className="flex-1">{item.label || t(item.key)}</span>
                            <FiExternalLink className="h-4 w-4 text-fbtt-gray" />
                          </a>
                        );
                      }
                      return (
                        <Link
                          key={item.key}
                          href={item.href}
                          className={`flex items-center gap-3 rounded-xl px-3 py-3.5 text-[15px] font-semibold transition-colors active:bg-fbtt-light ${
                            isActive ? "bg-fbtt-orange/10 text-fbtt-orange" : "text-fbtt-navy"
                          }`}
                        >
                          <Icon className="h-5 w-5 shrink-0 text-fbtt-orange" />
                          <span className="flex-1">{t(item.key)}</span>
                          <FiChevronRight className="h-4 w-4 text-fbtt-gray" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* Contact card */}
          <div className="border-t border-fbtt-navy/10 bg-fbtt-light/60 px-4 py-5">
            <p className="mb-3 px-2 text-[11px] font-bold uppercase tracking-widest text-fbtt-teal">
              Contact
            </p>
            <div className="space-y-2">
              <a
                href="tel:+59998699559"
                className="flex items-center gap-3 rounded-xl bg-white px-3 py-3 shadow-sm ring-1 ring-fbtt-navy/5 transition-colors active:bg-fbtt-light"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fbtt-orange/10">
                  <FiPhone className="h-4 w-4 text-fbtt-orange" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-fbtt-gray">Phone</p>
                  <p className="truncate text-sm font-semibold text-fbtt-navy">+5999 869 9559</p>
                </div>
              </a>
              <a
                href="mailto:info@fb-tt.com"
                className="flex items-center gap-3 rounded-xl bg-white px-3 py-3 shadow-sm ring-1 ring-fbtt-navy/5 transition-colors active:bg-fbtt-light"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fbtt-teal/10">
                  <FiMail className="h-4 w-4 text-fbtt-teal" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-fbtt-gray">Email</p>
                  <p className="truncate text-sm font-semibold text-fbtt-navy">info@fb-tt.com</p>
                </div>
              </a>
              <div className="flex items-center gap-3 rounded-xl bg-white px-3 py-3 shadow-sm ring-1 ring-fbtt-navy/5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fbtt-sky/15">
                  <FiMapPin className="h-4 w-4 text-fbtt-sky-dark" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-fbtt-gray">Address</p>
                  <p className="truncate text-sm font-semibold text-fbtt-navy">Seru di Mahuma, Curaçao</p>
                </div>
              </div>
            </div>
          </div>

          {/* Socials */}
          <div className="border-t border-fbtt-navy/10 px-4 py-5">
            <p className="mb-3 px-2 text-[11px] font-bold uppercase tracking-widest text-fbtt-teal">
              Follow us
            </p>
            <div className="flex gap-3 px-2">
              <a
                href="https://www.facebook.com/tabertours/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-fbtt-navy/5 text-fbtt-navy transition-colors hover:bg-[#1877F2] hover:text-white"
                aria-label="Facebook"
              >
                <FiFacebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/fbtttravel/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-fbtt-navy/5 text-fbtt-navy transition-colors hover:bg-gradient-to-br hover:from-[#f09433] hover:via-[#dc2743] hover:to-[#bc1888] hover:text-white"
                aria-label="Instagram"
              >
                <FiInstagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/channel/UCmPkOtmz_5HP38Qm3yElh_w"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-fbtt-navy/5 text-fbtt-navy transition-colors hover:bg-[#FF0000] hover:text-white"
                aria-label="YouTube"
              >
                <FiYoutube className="h-5 w-5" />
              </a>
              <a
                href="https://wa.me/59995146060"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-fbtt-navy/5 text-fbtt-navy transition-colors hover:bg-[#25D366] hover:text-white"
                aria-label="WhatsApp"
              >
                <FiMessageCircle className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Footer — language switcher */}
        <div className="relative shrink-0 border-t border-fbtt-navy/10 bg-fbtt-light p-3">
          <button
            onClick={() => setLangOpen((v) => !v)}
            className="flex w-full items-center gap-3 rounded-xl bg-white px-4 py-3 text-sm font-semibold text-fbtt-navy shadow-sm ring-1 ring-fbtt-navy/5 transition-colors active:bg-fbtt-light"
            aria-expanded={langOpen}
            aria-haspopup="listbox"
          >
            <FiGlobe className="h-4 w-4 text-fbtt-orange" />
            <span className="flex-1 text-left">{localeNames[locale]}</span>
            <span className="rounded-md bg-fbtt-navy/5 px-2 py-0.5 text-xs font-bold text-fbtt-navy/70">
              {localeLabels[locale]}
            </span>
            <FiChevronDown className={`h-4 w-4 text-fbtt-gray transition-transform ${langOpen ? "rotate-180" : ""}`} />
          </button>
          {langOpen && (
            <div className="absolute bottom-full left-3 right-3 mb-2 overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-fbtt-navy/10">
              {routing.locales.map((loc) => (
                <button
                  key={loc}
                  onClick={() => switchLocale(loc)}
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors ${
                    loc === locale
                      ? "bg-fbtt-orange/10 font-semibold text-fbtt-orange"
                      : "text-fbtt-navy active:bg-fbtt-light"
                  }`}
                  role="option"
                  aria-selected={loc === locale}
                >
                  <span className="w-10 text-xs font-bold text-fbtt-gray">{localeLabels[loc]}</span>
                  <span>{localeNames[loc]}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
